import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { SiteSettingsProvider } from "@/lib/siteSettings";

import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ServicesPoc from "@/pages/ServicesPoc";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import DiagnosticPoc from "@/pages/DiagnosticPoc";
import AuthCallback from "@/pages/AuthCallback";

import Home from "@/pages/Home";
import Neo from "@/pages/Neo";
import BuildMyPlan from "@/pages/BuildMyPlan";
import MyPlan from "@/pages/MyPlan";
import About from "@/pages/About";
import FloridaTransferSystem from "@/pages/FloridaTransferSystem";
import PlaybookUF from "@/pages/PlaybookUF";
import PlaybookUCF from "@/pages/PlaybookUCF";
import PlaybookHarvard from "@/pages/PlaybookHarvard";
import TransferAudit from "@/pages/TransferAudit";
import Pricing from "@/pages/Pricing";
import Resources from "@/pages/Resources";
import Guide from "@/pages/Guide";
import PortalShell, { PortalDashboard, PortalTools, PortalTranscripts, PortalDeadlines, PortalVault, PortalRoadmap, PortalAdvisor, PortalAccount } from "@/components/PortalShell";
import Tools from "@/pages/Tools";
import Playbook from "@/pages/Playbook";
import ToolEligibility from "@/pages/tools/Eligibility";
import ToolGpaForecast from "@/pages/tools/GpaForecast";
import ToolSemesterPlanner from "@/pages/tools/SemesterPlanner";
import ToolProbabilityDashboard from "@/pages/tools/ProbabilityDashboard";
import ToolDeadlines from "@/pages/tools/Deadlines";
import ToolTranscriptAudit from "@/pages/tools/TranscriptAudit";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import IvySignals from "@/pages/IvySignals";
import InstitutionIndex from "@/pages/InstitutionIndex";
import InstitutionDetail from "@/pages/InstitutionDetail";
import { MajorIndex, MajorDetail } from "@/pages/Majors";
import { RoadmapIndex, RoadmapDetail } from "@/pages/Roadmaps";
import TransferMap from "@/pages/TransferMap";
import NotFound from "@/pages/NotFound";

// Phase 5 — Admin CRM
import AdminShell from "@/components/AdminShell";
import AdminOverview from "@/pages/admin/Overview";
import AdminClients from "@/pages/admin/Clients";
import AdminClientDetail from "@/pages/admin/ClientDetail";
import AdminLeads from "@/pages/admin/Leads";
import AdminLeadDetail from "@/pages/admin/LeadDetail";
import AdminPayments from "@/pages/admin/Payments";
import AdminReferrals from "@/pages/admin/Referrals";
import AdminEmails from "@/pages/admin/Emails";
import AdminBlog from "@/pages/admin/Blog";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminMishapAnalyses from "@/pages/admin/AdminMishapAnalyses";
import AdminEntitlements from "@/pages/admin/AdminEntitlements";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSEO from "@/pages/admin/AdminSEO";
import AdviserPortal from "@/pages/AdviserPortal";
import MishapCorrector from "@/pages/MishapCorrector";
import MishapResults from "@/pages/MishapResults";
import TransferGuide from "@/pages/TransferGuide";
import MishapProgramLanding from "@/pages/MishapProgramLanding";
import PartnersILC from "@/pages/PartnersILC";
import MishapUnsupported from "@/pages/MishapUnsupported";

// Phase N — Company positioning pages
import Company from "@/pages/Company";
import Methodology from "@/pages/Methodology";
import Team from "@/pages/Team";
import Impact from "@/pages/Impact";
import Press from "@/pages/Press";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import Accessibility from "@/pages/Accessibility";

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Phase 2 marketing site */}
      <Route path="/" element={<Home />} />
      <Route path="/neo" element={<Neo />} />
      <Route path="/build-my-plan" element={<BuildMyPlan />} />
      <Route path="/my-plan/:plan_id" element={<MyPlan />} />
      <Route path="/about" element={<About />} />
      <Route path="/florida-transfer-system" element={<FloridaTransferSystem />} />
      <Route path="/playbooks/uf" element={<PlaybookUF />} />
      <Route path="/playbooks/ucf-directconnect" element={<PlaybookUCF />} />
      <Route path="/playbooks/harvard" element={<PlaybookHarvard />} />
      <Route path="/transfer-audit" element={<TransferAudit />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<Guide />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/tools/eligibility" element={<ToolEligibility />} />
      <Route path="/tools/gpa-forecast" element={<ToolGpaForecast />} />
      <Route path="/tools/semester-planner" element={<ToolSemesterPlanner />} />
      <Route path="/tools/probability-dashboard" element={<ToolProbabilityDashboard />} />
      <Route path="/tools/deadlines" element={<ToolDeadlines />} />
      <Route path="/tools/transcript-audit" element={<ToolTranscriptAudit />} />
      <Route path="/tools/ivy-signals" element={<Navigate to="/tools/probability-dashboard" replace />} />
      <Route path="/playbook" element={<Playbook />} />
      <Route path="/tools/ivy-signals-legacy" element={<IvySignals />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Phase F — Mishap Corrector */}
      <Route path="/mishap-corrector" element={<MishapCorrector />} />
      <Route path="/mishap-corrector/unsupported" element={<MishapUnsupported />} />
      <Route path="/mishap-corrector/results/:analysis_id" element={<MishapResults />} />
      <Route path="/mishap-corrector/programs/:slug" element={<MishapProgramLanding />} />

      {/* Phase L — Partnership */}
      <Route path="/partners/ivy-league-consulting" element={<PartnersILC />} />

      {/* Phase N — Company positioning */}
      <Route path="/company" element={<Company />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/team" element={<Team />} />
      <Route path="/impact" element={<Impact />} />
      <Route path="/press" element={<Press />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/accessibility" element={<Accessibility />} />

      {/* Phase K — programmatic pair pages */}
      <Route path="/transfer-guides/:source_slug/:destination_slug" element={<TransferGuide />} />

      {/* Priority 1.2 — Institution / Major / Roadmap catalog */}
      <Route path="/community-colleges" element={<InstitutionIndex variant="community_college" />} />
      <Route path="/community-colleges/:slug" element={<InstitutionDetail variant="community_college" />} />
      <Route path="/universities" element={<InstitutionIndex variant="state_university" />} />
      <Route path="/universities/:slug" element={<InstitutionDetail variant="state_university" />} />
      <Route path="/majors" element={<MajorIndex />} />
      <Route path="/majors/:slug" element={<MajorDetail />} />
      <Route path="/roadmaps" element={<RoadmapIndex />} />
      <Route path="/roadmaps/:slug" element={<RoadmapDetail />} />
      <Route path="/transfer-map" element={<TransferMap />} />

      <Route path="/contact" element={<Contact />} />

      {/* Phase 1 → Phase 2 redirects */}
      <Route path="/story" element={<Navigate to="/about" replace />} />
      <Route path="/services" element={<Navigate to="/pricing" replace />} />
      <Route path="/timeline" element={<Navigate to="/florida-transfer-system" replace />} />

      {/* Auth + post-auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<PortalShell />}>
        <Route index element={<PortalDashboard />} />
        <Route path="tools" element={<PortalTools />} />
        <Route path="transcripts" element={<PortalTranscripts />} />
        <Route path="deadlines" element={<PortalDeadlines />} />
        <Route path="vault" element={<PortalVault />} />
        <Route path="roadmap" element={<PortalRoadmap />} />
        <Route path="advisor" element={<PortalAdvisor />} />
        <Route path="account" element={<PortalAccount />} />
      </Route>
      <Route path="/dashboard-legacy" element={<Dashboard />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />

      {/* Phase 5 — Admin CRM */}
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<AdminOverview />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:user_id" element={<AdminClientDetail />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="leads/:lead_id" element={<AdminLeadDetail />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="referrals" element={<AdminReferrals />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="catalog/:entity" element={<AdminCatalog />} />
        <Route path="catalog" element={<Navigate to="/admin/catalog/institutions" replace />} />
        <Route path="verification" element={<AdminVerification />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="mishap-analyses" element={<AdminMishapAnalyses />} />
        <Route path="entitlements" element={<AdminEntitlements />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="seo" element={<AdminSEO />} />
      </Route>
      <Route path="/adviser/*" element={<AdviserPortal />} />

      {/* Phase 0 internal stubs (kept but unlinked from public nav) */}
      <Route path="/services-poc" element={<ServicesPoc />} />
      <Route path="/diagnostic-poc" element={<DiagnosticPoc />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </SiteSettingsProvider>
      </HelmetProvider>
    </div>
  );
}
